// Shared "email this proposal to the client" flow for the PPD proposal pages
// (ProposalsPPD, PPDSentProposals, PPDApprovedProposals, PPDCompletedProposals).
// Send flow: the saved PDF must exist, then the
// `sendProposalEmail` callable emails the property/proposal contacts from the
// Priority Pest Defense mailbox with the PDF attached.
import { getFunctions, httpsCallable } from "firebase/functions";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import { db } from "../firebaseConfig";

const STATUSES_TO_KEEP = ["Approved", "Completed", "Not Approved"];

export const describeProposalEmailError = (error) => {
  const code = String(error?.code || "").replace(/^functions\//, "");
  const message = String(error?.message || "").trim();
  if (message && !/^(internal|unknown)$/i.test(message)) return message;
  if (code === "unauthenticated") return "You must be signed in to email proposals.";
  if (code === "not-found") return "The sendProposalEmail function was not found. It may not be deployed.";
  if (code === "deadline-exceeded" || code === "unavailable") {
    return "The email server did not respond in time. Please try again.";
  }
  return "The proposal email could not be sent. Check the Priority email settings and try again.";
};

const toDate = (value) => {
  if (!value) return null;
  if (typeof value.toDate === "function") return value.toDate();
  if (typeof value.seconds === "number") return new Date(value.seconds * 1000);
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
};

const toList = (value) =>
  (Array.isArray(value) ? value : [value])
    .flatMap((v) => String(v || "").split(/[;,]/))
    .map((v) => v.trim())
    .filter(Boolean);

// One-line "last emailed" summary for button tooltips / details popups.
export const describeLastProposalEmail = (proposal = {}) => {
  const when = toDate(proposal.proposalEmailSentAt || proposal.emailedAt);
  const to = toList(proposal.proposalEmailRecipients || proposal.emailedTo);
  if (!when && !to.length) {
    return proposal.manuallyMarkedSent ? "Moved to Sent without emailing" : "";
  }
  return `Last emailed${when ? ` ${when.toLocaleString()}` : ""}${to.length ? ` to ${to.join(", ")}` : ""}`;
};

/**
 * Email a PPD proposal to the client.
 * Returns the local field patch to merge into page state, or null if cancelled/failed.
 * Errors are shown to the user here.
 */
export const sendPPDProposalEmail = async (proposal, { onStart, onFinish } = {}) => {
  if (!proposal?.id) return null;

  const isResend = Boolean(proposal.hasBeenSent && !proposal.manuallyMarkedSent) ||
    Boolean(proposal.proposalEmailSentAt || proposal.emailedAt);
  const confirmed = window.confirm(
    isResend
      ? "This proposal was already emailed. Email it to the client again?"
      : "Are you ready to email this proposal?"
  );
  if (!confirmed) return null;

  let started = false;
  try {
    const proposalRef = doc(db, "proposals", proposal.id);
    const freshSnap = await getDoc(proposalRef);
    const fresh = freshSnap.exists() ? { id: freshSnap.id, ...freshSnap.data() } : proposal;

    if (!fresh.latestProposalPdfUrl) {
      alert("Please open Details and click Print/Download first so the final PDF is saved before emailing.");
      return null;
    }

    started = true;
    if (onStart) onStart(proposal.id);

    const statusBefore = fresh.status || "Not Sent";
    const callable = httpsCallable(getFunctions(), "sendProposalEmail");
    const result = await callable({ proposalId: proposal.id });
    const recipients = toList(result?.data?.to);

    // A resend from Approved/Completed must not move the proposal back to Sent.
    let status = "Sent";
    if (STATUSES_TO_KEEP.includes(statusBefore)) {
      status = statusBefore;
      try {
        const after = await getDoc(proposalRef);
        if (after.exists() && after.data().status !== statusBefore) {
          await updateDoc(proposalRef, { status: statusBefore });
        }
      } catch (restoreError) {
        console.error("Could not restore proposal status after emailing:", restoreError);
      }
    }

    const sentAt = new Date().toISOString();
    alert(
      recipients.length
        ? `Proposal emailed to ${recipients.join(", ")}.`
        : "Proposal emailed."
    );

    return {
      status,
      hasBeenSent: true,
      proposalEmailStatus: "Sent",
      proposalEmailSentAt: sentAt,
      proposalEmailRecipients: recipients,
      emailedAt: sentAt,
      emailedTo: recipients.join(", "),
    };
  } catch (error) {
    console.error("Error sending proposal email:", error);
    alert(describeProposalEmailError(error));
    return null;
  } finally {
    if (started && onFinish) onFinish(proposal.id);
  }
};
